import _ from 'lodash';

type RemindersContent = Pick<Studio.ChartContent, 'visType' | 'description'> & {
  table?: Studio.TableOptions;
};

// Whether a Table lists reminders, saved as the legacy Reminders gadget
export const isRemindersTable = (content: RemindersContent): boolean =>
  content.visType === 'table' && content.table?.rows === 'reminders';

// The legacy Reminders gadget a reminders Table saves as and draws with
// (components/RemindersGadget). Dashboards only, as in the classic editor.
const buildRemindersDefinition = ({
  card,
  id,
  name,
  isReport = false,
}: {
  card: RemindersContent;
  id: string;
  name: string;
  isReport?: boolean;
}):
  | { chartDefinition: VisualisationDefinitions.Reminders }
  | { missing: string } => {
  if (isReport) {
    return { missing: 'Reminders are only available on dashboards' };
  }
  const events = card.table?.reminders?.events ?? [];
  if (events.length === 0) {
    return { missing: 'Choose the events to list' };
  }
  return {
    chartDefinition: {
      id,
      name,
      description: card.description ?? '',
      type: 'Reminders',
      events,
    },
  };
};

// A reminders Table's settings from a saved Reminders gadget
export const guessRemindersCard = (
  chartDef: VisualisationDefinitions.Reminders,
): Studio.GadgetSidecarCard => ({
  visType: 'table',
  name: chartDef.name,
  ...(chartDef.description ? { description: chartDef.description } : {}),
  table: { rows: 'reminders', reminders: { events: chartDef.events } },
});

// Whether the settings rebuild the saved Reminders gadget. Its name is the
// gadget's own, as is anything it keeps for soft deletes and the version
// older ones carry, which nothing reads.
export const reproducesReminders = ({
  card,
  chartDef,
}: {
  card: Studio.GadgetSidecarCard;
  chartDef: VisualisationDefinitions.Reminders;
}): boolean => {
  if (!isRemindersTable(card)) {
    return false;
  }
  const built = buildRemindersDefinition({
    card,
    id: chartDef.id,
    name: chartDef.name,
  });
  const comparable = (def: VisualisationDefinitions.Reminders) => ({
    ..._.omit(def, ['isHidden', 'hiddenOn', 'version']),
    description: def.description || undefined,
  });
  return (
    'chartDefinition' in built &&
    _.isEqual(comparable(built.chartDefinition), comparable(chartDef))
  );
};

export default buildRemindersDefinition;
